import {
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { ChartData } from "@lifeat/shared/data-models/user/UserMetrics/UserMetricsEntity";

ChartJS.register(BarController, CategoryScale, BarElement, PointElement, LinearScale, Title, Tooltip);

const GRID_CONFIG = {
    grid: {
        display: false,
    },
};

/**
 * Formats time in hours to readable time string - 1.5 -> 1h 30m
 * @param {number} time number of hours
 * @return {string} readable time string with hours and minutes
 */
const formatTime = (time: number) => {
    const hours = Math.floor(time);
    const minutes = (time - hours) * 60;
    const minutesRounded = Math.round(minutes * 100) / 100;
    return `${hours > 0 ? hours + `h ` : ``} ${minutesRounded + `m`}`;
};

/**
 * Formats numerical date string into readable date string - 20220818 -> Aug 18
 * @param {string} date numerical date string
 * @return {string} readable month and day date string
 */
const formatDateString = (date: string) => {
    if (date.length !== 8) {
        //invalid date, don't parse
        return "--";
    }

    // creating a date object with the numerical year, month, and date values
    // Ex: new Date(2022, 08, 18)
    const dateObj = new Date(parseInt(date.slice(0, 4)), parseInt(date.slice(4, 6)) - 1, parseInt(date.slice(6, 8)));
    if (!(dateObj instanceof Date)) {
        return "--";
    }
    return dateObj.toLocaleString("default", { month: "short", day: "numeric" });
};

interface BarChartProps {
    readonly numDays: number;
    readonly data: ChartData;
}

export default function BarChart(props: BarChartProps) {
    const data = {
        labels: props.data["x"].slice(-props.numDays).map((date: string) => {
            return formatDateString(date);
        }),
        datasets: [
            {
                backgroundColor: "#AECFFF",
                borderRadius: 5,
                data: props.data["y"].slice(-props.numDays),
            },
        ],
    };

    const options = {
        scaleShowLabels: false,
        scales: {
            x: {
                ...GRID_CONFIG,
                ticks: {
                    color: "#919498",
                },
            },
            y: {
                ...GRID_CONFIG,
                ticks: {
                    color: "#919498",
                    callback: function (value: any) {
                        return formatTime(value);
                    },
                },
            },
        },
        plugins: {
            tooltip: {
                callbacks: {
                    // reference src: https://www.chartjs.org/docs/latest/configuration/tooltip.html#label-callback
                    // callback function to display time as a human-readable string in tooltip label
                    label: function (tooltipItem: any) {
                        return formatTime(tooltipItem.parsed["y"]);
                    },
                },
            },
            legend: { display: false },
        },
    };

    return <Chart type="bar" data={data} options={options} />;
}
